import styled from "styled-components";
import {IconButtonV5} from "../../../interaction/IconButtonV5";
import XmarkIcon from "../../../icons/xmark.svg?react";
import {useAppStore} from "../../../../../StateManager";
import {baseDistance, fontSize, fontWeight, radius, VerticalFlexStyle, zIndex} from "../../../styles/styles";
import {colorPalette} from "../../../styles/colorPalette";
import type {MissingField} from "./CatalogItemPanelUtils";

interface IPartialSuccessProps {
	readonly missingFields: MissingField[];
	readonly onClose?: () => void;
}

export const PartialSucesspopupV5 = (props: IPartialSuccessProps) => {
	const appState = useAppStore((state) => state.appState);
	const missingFields: {fieldKey: string; missingValues: string[]}[] = props.missingFields || [];

	return (
		<PartialSucessPopupStyled>
			<div className="header">
				<p>The catalog item was partially duplicated</p>
				<IconButtonV5
					IconComponent={XmarkIcon}
					onClick={props.onClose}
				/>
			</div>
			<p className="sub-header">Following values were removed as they are no longer valid.</p>

			<table>
				<thead>
					<tr>
						<th>Field</th>
						<th>Values</th>
					</tr>
				</thead>
				<tbody>
					{missingFields.map((item, index) => {
						const field = appState.actions.getFieldByRefId(item.fieldKey);

						return (
							<tr key={index}>
								<td>{field?.name || item.fieldKey}</td>
								<td>
									{item.missingValues.map((val, i) => {
										const isNotLast = i < item.missingValues.length - 1;

										return (
											<span
												key={i}
												className="value"
											>
												{val}
												{isNotLast ? ",\u00A0" : ""}
											</span>
										);
									})}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</PartialSucessPopupStyled>
	);
};

const PartialSucessPopupStyled = styled.div`
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	border-radius: ${radius.md};
	background-color: ${colorPalette.white};
	box-shadow: 0px 4px 8px 0px #00000080;
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.sm};
	padding: ${baseDistance.md};
	z-index: ${zIndex.popup};
	width: 800px;
	min-height: 300px;
	max-height: 800px;
	flex: 1;

	.header {
		display: flex;
		justify-content: space-between;

		p {
			font-size: 24px;
			font-weight: ${fontWeight.bold};
		}
	}

	.sub-header {
		font-size: ${fontSize.lg};
		font-weight: ${fontWeight.normal};
		color: ${colorPalette.gray.c700Dark};
	}

	table {
		width: 100%;
		border-collapse: separate;
		margin-top: 1rem;
		border-radius: ${radius.md};
		table-layout: fixed;
		${VerticalFlexStyle}
		flex: 1;
		min-height: 0;
		overflow-y: auto;
	}

	thead,
	th {
		position: sticky;
		top: 0;
	}

	th,
	td {
		border: 1px solid ${colorPalette.gray.c100};
		padding: 8px;
		text-align: left;
		font-size: ${fontSize.lg};
		vertical-align: top;
	}

	th {
		min-height: 40px;
		color: ${colorPalette.gray.c950};
		font-weight: ${fontWeight.normal};
	}

	th:first-child {
		border-style: solid none solid solid;
	}

	td {
		color: ${colorPalette.gray.c700Dark};
		border-style: none solid solid solid;
	}

	td:first-child {
		color: ${colorPalette.gray.c700Dark};
		border-style: none none solid solid;
	}

	th:first-child,
	td:first-child {
		width: 200px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	thead,
	tbody tr {
		display: table;
		width: 100%;
		table-layout: fixed;
		background-color: ${colorPalette.white};
	}

	td .value {
		display: inline-block;
		max-width: 540px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		vertical-align: bottom;
	}

	thead tr:first-child th:first-child {
		border-top-left-radius: ${radius.md};
	}

	thead tr:first-child th:last-child {
		border-top-right-radius: ${radius.md};
	}

	tbody tr:last-child td:first-child {
		border-bottom-left-radius: ${radius.md};
	}

	tbody tr:last-child td:last-child {
		border-bottom-right-radius: ${radius.md};
	}
`;
